import { CognitoUser } from 'amazon-cognito-identity-js';
import { Auth } from 'aws-amplify';

import { getChromeRuntime } from './chromeExtension';

const EXT_ID_STORAGE_KEY = 'ext_id';

type ExtensionAuthPayload = {
  user: {
    id: string;
    name: string;
    picture: string;
  };
  refreshToken: string;
  token: string;
};

/**
 * Saves the extension ID to localStorage.
 */
export const saveExtensionId = (extId: string): void => {
  localStorage.setItem(EXT_ID_STORAGE_KEY, extId);
};

/**
 * Retrieves the saved extension ID from localStorage.
 */
export const getExtensionId = (): null | string => {
  return localStorage.getItem(EXT_ID_STORAGE_KEY);
};

/**
 * Removes the extension ID from localStorage.
 */
export const clearExtensionId = (): void => {
  localStorage.removeItem(EXT_ID_STORAGE_KEY);
};

/**
 * Sends auth credentials directly to the Chrome extension via externally_connectable.
 * This is called after successful authorization if an extension ID is present in localStorage.
 */
export const redirectToExtension = async (userId?: string): Promise<void> => {
  try {
    const extId = getExtensionId();
    if (!extId) return;

    const runtime = getChromeRuntime();
    if (!runtime) {
      throw new Error('Chrome runtime not available');
    }

    const cognitoUser = (await Auth.currentAuthenticatedUser()) as CognitoUser;
    const session = cognitoUser.getSignInUserSession();

    if (!session) {
      throw new Error('No active session found');
    }

    const idTokenPayload = session.getIdToken().payload;

    const payload: ExtensionAuthPayload = {
      refreshToken: session.getRefreshToken().getToken(),
      token: session.getIdToken().getJwtToken(),
      user: {
        id: userId || (idTokenPayload.sub as string) || '',
        name: idTokenPayload.name || '',
        picture: idTokenPayload.picture || '',
      },
    };

    await runtime.sendMessage(extId, { action: 'auth', payload });
    clearExtensionId();
  } catch (error) {
    // eslint-disable-next-line no-console
    console.error('Extension redirect failed:', error);
    clearExtensionId();
  }
};
